<template>
    <v-form class="steps-form" ref="form">
        <v-divider></v-divider>
        <v-alert v-if="anyError"
                 type="error"
                 color="red"
                 v-html="anyError"
                 rounded="0"
                 class="mb-0"
        ></v-alert>
        <v-alert v-if="unsavedFound"
                 dismissible
                 type="success"
                 rounded="0"
                 class="mb-0"
                 @close="this.unsavedFound = false"
        >
            <v-row align="center">
                <v-col class="grow">
                    Найдены несохраненные данные редактирования. Загрузить?
                </v-col>
                <v-col class="shrink">
                    <v-btn @click="loadUnsaved" color="white" text elevation="0">Загрузить</v-btn>
                </v-col>
            </v-row>
        </v-alert>

        <v-tabs vertical v-model="tab" show-arrows class="steps-form__menu">
            <v-tab href="#upload">Загрузка файлов</v-tab>
            <v-tab href="#grid" :disabled="isUploading">
                <v-icon v-if="!isGridValid" left>mdi-alert-circle-outline</v-icon>
                Параметры сетки
            </v-tab>
            <v-tab href="#shape" :disabled="isUploading || !gridHasData">
                <v-icon v-if="!isShapeValid" left>mdi-alert-circle-outline</v-icon>
                Форма залежи
            </v-tab>
            <v-tab href="#wells" :disabled="isUploading || !gridHasData">
                <v-icon v-if="!isWellsValid || !isConnectionsValid" left>mdi-alert-circle-outline</v-icon>
                 Скважины
            </v-tab>
            <v-tab href="#swso" :disabled="isUploading">
                <v-icon v-if="!isSwSoValid" left>mdi-alert-circle-outline</v-icon>
                Начальные S<sub>w</sub> и S<sub>o</sub>
            </v-tab>
            <v-tab href="#p" :disabled="isUploading">
                <v-icon v-if="!isPValid" left>mdi-alert-circle-outline</v-icon>
                Начальное Р<sub>пл</sub>
            </v-tab>
            <v-tab href="#fluid" :disabled="isUploading">
                <v-icon v-if="!isFluidValid" left>mdi-alert-circle-outline</v-icon>
                Свойства флюидов
            </v-tab>
            <v-tab href="#ofp" :disabled="isUploading">
                <v-icon v-if="!isOfpValid" left>mdi-alert-circle-outline</v-icon>
                ОФП
            </v-tab>
            <v-tab href="#rock" :disabled="isUploading || !gridHasData">
                <v-icon v-if="!isRockValid" left>mdi-alert-circle-outline</v-icon>
                Свойства породы
            </v-tab>
            <v-tab href="#injectionData" :disabled="isUploading">
                <v-icon v-if="!isInjectionDataValid" left>mdi-alert-circle-outline</v-icon>
                История разработки
            </v-tab>
            <v-tab href="#historyData" :disabled="isUploading">
                <v-icon v-if="!isHistoryDataValid" left>mdi-alert-circle-outline</v-icon>
                История добычи
            </v-tab>
            <v-tab href="#bhpData" :disabled="isUploading">
                <v-icon v-if="!isBhpDataValid" left>mdi-alert-circle-outline</v-icon>
                История давлений
            </v-tab>
            <v-spacer></v-spacer>
            <v-menu top offset-y v-if="allErrors && allErrors.length > 0">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn color="red" dark v-bind="attrs" v-on="on" elevation="0" class="ml-4">
                        Ошибки <v-chip color="white" text-color="red" small class="px-1 ml-2">{{allErrors.length}}</v-chip>
                    </v-btn>
                </template>

                <v-list>
                    <v-list-item v-for="(error, index) in allErrors" :key="index" color="red">
                        <v-list-item-title>{{ error.error }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
            <v-btn text elevation="0" class="ml-4" disabled v-else>Ошибок нет</v-btn>

            <v-tabs-items v-model="tab" class="allow-overflow">
                <step tab-code="upload"
                      title="Загрузка файлов"
                      :value="oneStepFiles"
                      :error="oneStepFileError"
                      :is-valid="isValid"
                      :is-uploading="isUploading"
                      :enable-upload="true"
                      :show-files="true"
                      @input="setOneStepFiles"
                      description="Вы можете выбрать один или несколько файлов с описанием модели в формате Eclipse"
                >
                    <template #content v-if="!isUploading">
                        <v-radio-group v-model="uploadType">
                            <v-radio value="file" label="Загрузить данные"></v-radio>
                            <v-radio value="manual" label="Ввести вручную"></v-radio>
                        </v-radio-group>
                    </template>
                    <template #content v-else>
                        <div><v-progress-circular indeterminate color="primary"></v-progress-circular></div>
                    </template>
                    <template #upload v-if="(!isUploading && uploadType === 'manual') || (isUploading)"><span></span></template>
                    <template #extra v-if="!isUploading && uploadType === 'manual'">
                        <v-btn @click="tab = 'grid'" elevation="0" color="primary">Ввести данные</v-btn>
                    </template>
                </step>
                <step tab-code="grid"
                      title="Параметры сетки"
                      v-model="gridFiles"
                      :input-has-data="gridHasData"
                      :is-valid="isGridValid"
                      :is-uploading="isUploading"
                      description="Файл с расширением .DATA"
                >
                    <template #content>
                        <v-sheet class="grid-table mb-4">
                            <v-row class="grid-header"><v-col>Размерность</v-col><v-col>Кол-во ячеек</v-col></v-row>
                            <v-row>
                                <v-col>
                                    <v-text-field dense hide-details type="number" min="0" v-model="gridCellSize[0]"
                                                  :rules="validationRules.gtZero"
                                                  :disabled="isParsed"
                                                  ref="input_grid_size_x"
                                    >
                                        <template #prepend>X</template>
                                        <template #append-outer><span class="text-muted">м</span></template>
                                    </v-text-field>
                                </v-col>
                                <v-col>
                                    <v-text-field dense hide-details type="number" min="0" v-model="gridSize[0]"
                                                  :rules="validationRules.gtZero"
                                                  :disabled="isParsed"
                                                  ref="input_grid_x"
                                    >
                                        <template #prepend>По X</template>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <v-text-field dense hide-details type="number" min="0" v-model="gridCellSize[1]"
                                                  ref="input_grid_size_y"
                                                  :disabled="isParsed"
                                                  :rules="validationRules.gtZero"
                                    >
                                        <template #prepend>Y</template>
                                        <template #append-outer><span class="text-muted">м</span></template>
                                    </v-text-field>
                                </v-col>
                                <v-col>
                                    <v-text-field dense hide-details type="number" min="0" v-model="gridSize[1]"
                                                  ref="input_grid_y"
                                                  :disabled="isParsed"
                                                  :rules="validationRules.gtZero"
                                    >
                                        <template #prepend>По Y</template>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <v-text-field dense hide-details type="number" min="0" v-model="gridCellSize[2]"
                                                  ref="input_grid_size_z"
                                                  :disabled="isParsed"
                                                  :rules="validationRules.gtZero"
                                    >
                                        <template #prepend>Z</template>
                                        <template #append-outer><span class="text-muted">м</span></template>
                                    </v-text-field>
                                </v-col>
                                <v-col>
                                    <v-text-field dense hide-details type="number" min="0" v-model="gridSize[2]"
                                                  ref="input_grid_z"
                                                  :disabled="isParsed"
                                                  :rules="validationRules.gtZero"
                                    >
                                        <template #prepend>По Z</template>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-sheet>
                    </template>
                </step>
                <step tab-code="shape"
                      title="Форма залежи"
                      v-model="shapeFiles"
                      :is-valid="isShapeValid"
                      :is-uploading="isUploading"
                      description="Данные берутся из куба ACTNUM файлов гидродинамического симулятора"
                >
                    <template #default>
                        <v-sheet ref="wrapper" class="fill-height d-flex flex-column">
                            <h2 class="page-title page-title__primary">Форма залежи</h2>
                            <p>Данные берутся из куба ACTNUM файлов гидродинамического симулятора</p>
                            <div class="grid-wrapper">
                                <grid v-if="tab === 'shape'"
                                      :problem-id="item.id"
                                      :problem="item"
                                      :input-size="gridSize"
                                      :input-cell-size="gridCellSize"
                                      :input-show-mesh="true"
                                      :is-parsed="isParsed"
                                      :grid-mesh-file="gridMeshFile"
                                ></grid>
                            </div>
                        </v-sheet>
                    </template>
                </step>
                <step tab-code="wells"
                      v-model="wellFiles"
                      :is-valid="isWellsValid && isConnectionsValid"
                      :is-uploading="isUploading"
                >
                    <template #default>
                        <wells v-model="wells"
                               ref="wells"
                               :grid-size="gridSize"
                               :grid-cell-size="gridCellSize"
                               :grid-files="allFilesText"
                               :grid-properties="gridProperties"
                               :connections="editConnections"
                               :nodes="editNodes"
                               :is-parsed="isParsed"
                               :problem="unsavedProblem"
                               :input-constants="allConstants"
                               @deleteWell="deleteWell"
                               @input-connections="setConnections"
                               @connections="method => updateConnections(method, true)"
                               @active="wellId => activeWellId = wellId"
                               @error="setConnectionsError"
                        >
                            <template #grid="{wc}">
                                <grid :problem-id="unsavedProblem.id"
                                      :input-wells="wells"
                                      :input-show-wells="true"
                                      :input-show-mesh="false"
                                      :input-show-connections="true"
                                      :connections="editConnections"
                                      :injection-data="injectionData"
                                      :nodes="editNodes"
                                      :is-parsed="isParsed"
                                      :grid-mesh-file="gridMeshFile"
                                      :active-well-id="activeWellId"
                                      :editable="true"
                                      :show-extra-menu="true"
                                      @clickWell="editWell"
                                      @addWell="addWellByCoords"
                                      @deleteWell="deleteWell"
                                      @addConnection="addConnection"
                                      @deleteConnection="deleteConnection"
                                >
                                    <template #extraMenu>
                                        <v-card>
                                            <v-card-text>
                                                <div class="d-flex flex-row mb-4 w-100">
                                                    <v-btn-toggle class="w-100">
                                                        <v-btn color="primary" elevation="0" dark :loading="wc.isConnectionsLoading"
                                                               @click="wc.loadConnections">
                                                            Перестроить соединения ({{ wc.activeBuilderTitle }})
                                                        </v-btn>
                                                        <v-menu offset-y>
                                                            <template v-slot:activator="{ on, attrs }">
                                                                <v-btn color="primary" elevation="0" dark icon v-bind="attrs" v-on="on">
                                                                    <v-icon>mdi-cog</v-icon>
                                                                </v-btn>
                                                            </template>
                                                            <v-list>
                                                                <v-list-item v-for="item in wc.connectionBuilderTypes" :key="item.value"
                                                                             @click="wc.connectionBuilderType = item.value"
                                                                >
                                                                    <v-list-item-title>{{ item.text }}</v-list-item-title>
                                                                </v-list-item>
                                                            </v-list>
                                                        </v-menu>
                                                    </v-btn-toggle>
                                                    <div v-if="wc.showConnectionsUpdate" class="d-flex text--disabled ml-4 align-center">
                                                        Возможно, соединения следует обновить
                                                    </div>
                                                </div>
                                                <div class="d-flex flex-row mb-4 w-100">
                                                    <v-btn-toggle class="w-100">
                                                        <v-btn color="primary" @click="wc.addImaginaryWells" elevation="0" class="" outlined
                                                               :loading="wc.imaginaryLoading">
                                                            Добавить мнимые скважины
                                                            ({{ wc.activeImaginaryBuilderTitle }}, {{
                                                                wc.imaginaryCount
                                                            }} шт)
                                                        </v-btn>
                                                        <v-menu offset-y :close-on-content-click="false">
                                                            <template #activator="{ on, attrs }">
                                                                <v-btn color="primary" elevation="0" dark icon v-bind="attrs" v-on="on">
                                                                    <v-icon>mdi-cog</v-icon>
                                                                </v-btn>
                                                            </template>
                                                            <v-card>
                                                                <v-card-text>
                                                                    <v-text-field type="number" v-model="wc.imaginaryCount"></v-text-field>
                                                                </v-card-text>
                                                                <v-list>
                                                                    <v-list-item v-for="item in wc.imaginaryBuilderTypes" :key="item.value"
                                                                                 @click="wc.imaginaryBuilderType = item.value"
                                                                    >
                                                                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                                                                    </v-list-item>
                                                                </v-list>
                                                            </v-card>
                                                        </v-menu>
                                                    </v-btn-toggle>
                                                </div>
                                                <div class="d-flex flex-row mb-4 w-100">
                                                    <v-menu offset-y :close-on-content-click="false" v-model="showCopyWellsMenu">
                                                        <template #activator="{ on, attrs }">
                                                            <v-btn color="primary" elevation="0" outlined v-bind="attrs" v-on="on">
                                                                Копировать скважины из другой задачи
                                                            </v-btn>
                                                        </template>
                                                        <v-card>
                                                            <v-list>
                                                                <v-list-item v-for="item in wellCopyItems" :key="item.id"
                                                                             @click="copyWells(wc, item)"
                                                                >
                                                                    <v-list-item-title>{{ item.problem.name }}</v-list-item-title>
                                                                </v-list-item>
                                                            </v-list>
                                                        </v-card>
                                                    </v-menu>
                                                </div>

                                                <div class="d-flex flex-row">
                                                    <v-btn color="primary" block @click="wc.recalcAll" elevation="0" outlined
                                                           :loading="wc.recalcLoading">Пересчитать все
                                                    </v-btn>
                                                </div>
                                            </v-card-text>
                                        </v-card>
                                    </template>
                                </grid>
                            </template>
                        </wells>
                    </template>
                </step>
                <step tab-code="swso"
                      title="Начальные S<sub>w</sub> и S<sub>o</sub>"
                      v-model="swsoFiles"
                      :is-valid="isSwSoValid"
                      :is-uploading="isUploading"
                      description="Берутся из куба SWATINIT гидродинамического симулятора, либо из секции SWOF"
                >
                    <template #content>
                        <v-sheet class="mb-8 input-fields">
                            <v-row no-gutters>
                                <v-col>
                                    <v-text-field dense hint="Неуменьшаемая водонасыщенность" persistent-hint type="number"
                                                  min="0" max="1" step="0.1" v-model="inputConstants['sw']"
                                                  ref="input_sw"
                                                  :rules="validationRules.zeroToOne"
                                    >
                                        <template #prepend><p>S<sub>w</sub></p></template>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row no-gutters>
                                <v-col>
                                    <v-text-field dense hint="Остаточная водонасыщенность" persistent-hint type="number"
                                                  min="0" max="1" step="0.1" v-model="inputConstants['so']"
                                                  ref="input_so"
                                                  :rules="validationRules.zeroToOne"
                                    >
                                        <template #prepend><p>S<sub>o</sub></p></template>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-sheet>
                    </template>
                </step>
                <step tab-code="p"
                      title="Начальное Р<sub>пл</sub>"
                      v-model="pFiles"
                      :is-valid="isPValid"
                      :is-uploading="isUploading"
                      description="Значение берется из ключевого слова EQUIL раздела SOLUTION"
                >
                    <template #content>
                        <v-sheet class="mb-8 input-fields">
                            <v-row no-gutters>
                                <v-col>
                                    <v-text-field dense hint="Давление пласта" persistent-hint type="number"
                                                  min="0" v-model="inputConstants['p']"
                                                  ref="input_p"
                                                  :rules="validationRules.gtZero"
                                    >
                                        <template #prepend><p>P<sub>пл</sub></p></template>
                                        <template #append-outer><span class="text-muted">бар</span></template>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row no-gutters>
                                <v-col>
                                    <v-text-field dense hint="Глубина приведения" persistent-hint type="number"
                                                  ref="input_dd"
                                                  min="0" v-model="inputConstants['dd']"
                                                  :rules="validationRules.gtZero"
                                    >
                                        <template #prepend><p>D<sub>d</sub></p></template>
                                        <template #append-outer><span class="text-muted">м</span></template>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-sheet>
                    </template>
                </step>
                <step tab-code="fluid"
                      title="Свойства флюидов"
                      v-model="fluidFiles"
                      :is-valid="isFluidValid"
                      :is-uploading="isUploading"
                      :description="'Значение берутся из секций PVTO, PVDO, PVCO, PVCDO, PVTW' + (needDensity ? ', DENSITY' : '')"
                >
                    <template #content>
                        <v-sheet class="mb-8 input-fields">
                            <h3 class="page-title page-title__primary">Вода</h3>
                            <v-row no-gutters>
                                <v-col>
                                    <v-text-field dense hint="Вязкость воды" persistent-hint type="number"
                                                  min="0" v-model="inputConstants['mu_w']"
                                                  ref="input_mu_w"
                                                  :rules="validationRules.gtZero"
                                    >
                                        <template #prepend><p>&mu;<sub>W</sub></p></template>
                                        <template #append-outer><span class="text-muted">мПа/сек</span></template>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row no-gutters>
                                <v-col>
                                    <v-text-field dense hint="Сжимаемость воды" persistent-hint type="number"
                                                  ref="input_cw"
                                                  min="0" v-model="inputConstants['cw']"
                                                  :rules="validationRules.gtZero"
                                    >
                                        <template #prepend><p>c<sub>W</sub></p></template>
                                        <template #append-outer><span class="text-muted">бар<sup>-1</sup></span></template>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row no-gutters v-if="needDensity">
                                <v-col>
                                    <v-text-field dense hint="Плотность воды при стандартных условиях" persistent-hint type="number"
                                                  ref="input_rho_w_sc"
                                                  min="0" v-model="inputConstants['rho_w_sc']"
                                                  :rules="validationRules.gtZero"
                                    >
                                        <template #prepend><p>&rho;<sub>W</sub><sup>ст</sup></p></template>
                                        <template #append-outer><span class="text-muted">кг/м<sup>3</sup></span></template>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row no-gutters v-if="needDensity">
                                <v-col>
                                    <v-text-field dense hint="Плотность воды в условиях пласта" persistent-hint type="number"
                                                  ref="input_rho_w"
                                                  min="0" v-model="inputConstants['rho_w']"
                                                  :rules="validationRules.gtZero"
                                    >
                                        <template #prepend><p>&rho;<sub>W</sub></p></template>
                                        <template #append-outer><span class="text-muted">кг/м<sup>3</sup></span></template>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <h3 class="page-title page-title__primary mt-8">Нефть</h3>
                            <v-row no-gutters>
                                <v-col>
                                    <v-text-field dense hint="Вязкость нефти" persistent-hint type="number"
                                                  ref="input_mu_o"
                                                  min="0" v-model="inputConstants['mu_o']"
                                                  :rules="validationRules.gtZero"
                                    >
                                        <template #prepend><p>&mu;<sub>O</sub></p></template>
                                        <template #append-outer><span class="text-muted">мПа/сек</span></template>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row no-gutters>
                                <v-col>
                                    <v-text-field dense hint="Сжимаемость нефти" persistent-hint type="number"
                                                  ref="input_co"
                                                  min="0" v-model="inputConstants['co']"
                                                  :rules="validationRules.gtZero"
                                    >
                                        <template #prepend><p>c<sub>O</sub></p></template>
                                        <template #append-outer><span class="text-muted">бар<sup>-1</sup></span></template>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row no-gutters v-if="needDensity">
                                <v-col>
                                    <v-text-field dense hint="Плотность нефти при стандартных условиях" persistent-hint type="number"
                                                  ref="input_rho_o_sc"
                                                  min="0" v-model="inputConstants['rho_o_sc']"
                                                  :rules="validationRules.gtZero"
                                    >
                                        <template #prepend><p>&rho;<sub>O</sub><sup>ст</sup></p></template>
                                        <template #append-outer><span class="text-muted">кг/м<sup>3</sup></span></template>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row no-gutters v-if="needDensity">
                                <v-col>
                                    <v-text-field dense hint="Плотность нефти в условиях пласта" persistent-hint type="number"
                                                  ref="input_rho_o"
                                                  min="0" v-model="inputConstants['rho_o']"
                                                  :rules="validationRules.gtZero"
                                    >
                                        <template #prepend><p>&rho;<sub>O</sub></p></template>
                                        <template #append-outer><span class="text-muted">кг/м<sup>3</sup></span></template>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-sheet>
                    </template>
                </step>
                <step tab-code="ofp"
                      title="Относительные фазовые проницаемости"
                      :is-valid="isOfpValid"
                >
                    <template #description>
                        <p>Начальные значения коэффициентоц уравнений ОФП: a, n<sub>o</sub>, n<sub>w</sub></p>
                        <p>Значения этих коэффициентов будут изменяться в ходе адаптации. Конечные значения можно будет посмотреть в результатах расчета задачи.</p>
                        <p><vue-mathjax formula="$k_{rw}(S_w)=a \cdot S_w^{n_w}$"></vue-mathjax></p>
                        <p><vue-mathjax formula="$k_{ro}(S_w)=S_w^{n_o}$"></vue-mathjax></p>
                    </template>
                    <template #content>
                        <ofp v-model="ofpConstants" :siw="inputConstants['sw']" :sor="inputConstants['so']"></ofp>
                    </template>
                </step>
                <step tab-code="rock"
                      title="Свойства породы"
                      v-model="rockFiles"
                      :is-valid="isRockValid"
                      :is-uploading="isUploading"
                      description="Файлы с секциями PERMX, PERMY, PERMZ, PORO"
                >
                    <template #content>
                        <v-sheet class="mb-8 input-fields">
                            <v-row no-gutters>
                                <v-col>
                                    <v-text-field dense hint="Сжимаемость породы" persistent-hint type="number"
                                                  ref="input_cr"
                                                  min="0" v-model="inputConstants['cr']"
                                                  :rules="validationRules.gtZero"
                                    >
                                        <template #prepend><p>c<sub>R</sub></p></template>
                                        <template #append-outer><span class="text-muted">бар<sup>-1</sup></span></template>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row no-gutters>
                                <v-col>
                                    <v-text-field v-if="permeabilityDefinedInCells"
                                                  dense
                                                  disabled
                                                  :value="meanCellPermeability"
                                                  hint="Средняя проницаемость"
                                                  persistent-hint>
                                        <template #prepend>k</template>
                                        <template #append-outer><span class="text-muted">мД</span></template>
                                    </v-text-field>
                                    <v-text-field v-else
                                                  dense hint="Средняя проницаемость" persistent-hint type="number"
                                                  min="0" v-model="inputConstants['k']"
                                                  ref="input_k"
                                                  :rules="validationRules.gtZero"
                                    >
                                        <template #prepend>k</template>
                                        <template #append-outer><span class="text-muted">мД</span></template>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row no-gutters>
                                <v-col>
                                    <v-text-field v-if="porosityDefinedInCells"
                                                  dense
                                                  disabled
                                                  :value="meanCellPorosity"
                                                  hint="Средняя пористость"
                                                  persistent-hint>
                                        <template #prepend>&phi;</template>
                                    </v-text-field>
                                    <v-text-field v-else
                                                  dense hint="Средняя пористость" persistent-hint type="number"
                                                  ref="input_phi"
                                                  min="0" v-model="inputConstants['phi']"
                                                  :rules="validationRules.gtZero"
                                    >
                                        <template #prepend>&phi;</template>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-sheet>
                    </template>
                </step>
                <step tab-code="injectionData"
                      title="История разработки"
                      v-model="injectionDataFile"
                      :single="true"
                      :is-valid="isInjectionDataValid"
                      :is-uploading="isUploading"
                      :enable-upload="true"
                      description="Файл должен содержать исторические данные о закачке воды в скважины. Первая строка файла должна содержать названия скважин. Данные должны быть указаны в м<sup>3</sup>/день. У нагнетающих скважин значения должны быть положительные. У добывающих скважин &mdash; отрицательные. В файле должен быть столбец t с обозначением времени измерения в днях от начала наблюдений."
                >
                    <template #content>
                        <div class="grid-table">
                            <div>Тип данных:</div>
                            <div>
                                <v-radio-group row v-model="injectionDataType" class="mt-0">
                                    <v-radio label="CSV" value="csv" color="secondary"></v-radio>
                                    <v-radio
                                        label="Из ГДМ"
                                        value="hdm"
                                        color="secondary"
                                        :disabled="!injectionDataHdm"
                                    >
                                        <template #label v-if="!injectionData && historyOrInjectionError">
                                            <div>
                                                <p class="ma-0">Из ГДМ</p>
                                                <p class="ma-0 text--disabled text--small">
                                                    {{historyOrInjectionError}}
                                                </p>
                                            </div>
                                        </template>
                                    </v-radio>
                                </v-radio-group>
                            </div>
                            <history v-model="injectionData" :wells="realWells">
                                <template #buttons="{rows, titles, setRows}">
                                    <v-btn class="mb-2 mr-2"
                                           color="primary"
                                           elevation="0"
                                           @click="fillHistory(rows, titles, setRows, true)"
                                    >Заполнить пустые данные</v-btn>
                                </template>
                            </history>
                        </div>
                    </template>
                </step>
                <step tab-code="historyData"
                      title="История добычи"
                      v-model="historyDataFile"
                      :single="true"
                      :is-valid="isHistoryDataValid"
                      :is-uploading="isUploading"
                      :enable-upload="true"
                      description="Файл должен содержать исторические данные о добыче нефти из скважин. Первая строка файла должна содержать названия скважин. Данные должны быть указаны в м<sup>3</sup>/день. В файле должен быть столбец t с обозначением времени измерения в днях от начала наблюдений"
                >
                    <template #content>
                        <div class="grid-table">
                            <div>Тип данных:</div>
                            <div>
                                <v-radio-group row v-model="historyDataType" class="mt-0">
                                    <v-radio label="CSV" value="csv" color="secondary"></v-radio>
                                    <v-radio label="Из ГДМ" value="hdm" color="secondary" :disabled="!historyDataHdm">
                                        <template #label v-if="!historyDataHdm && historyOrInjectionError">
                                            <div>
                                                <p class="ma-0">Из ГДМ</p>
                                                <p class="ma-0 text--disabled text--small">
                                                    {{historyOrInjectionError}}
                                                </p>
                                            </div>
                                        </template>
                                    </v-radio>
                                </v-radio-group>
                            </div>
                            <history v-model="historyData" :wells="realWells" :some-wells="true">
                                <template #buttons="{rows, titles, setRows}">
                                    <v-btn class="mb-2 mr-2"
                                           color="primary"
                                           elevation="0"
                                           @click="fillHistory(rows, titles, setRows)"
                                    >Заполнить пустые данные</v-btn>
                                </template>
                            </history>
                        </div>
                    </template>
                </step>
                <step tab-code="bhpData"
                      title="История давлений"
                      v-model="bhpDataFile"
                      :single="true"
                      :is-valid="isBhpDataValid"
                      :is-uploading="isUploading"
                      :enable-upload="true"
                      description="Файл должен содержать исторические данные о давлениях на скважинах. Первая строка файла должна содержать названия скважин. Данные должны быть указаны в барах. В файле должен быть столбец t с обозначением времени измерения в днях от начала наблюдений."
                >
                    <template #content>
                        <v-sheet class="grid-table">
                            <div>Тип данных:</div>
                            <div>
                                <v-radio-group row v-model="bhpDataType" class="mt-0">
                                    <v-radio label="CSV" value="csv" color="secondary"></v-radio>
                                    <v-radio
                                        label="Из ГДМ"
                                        value="hdm"
                                        color="secondary"
                                        :disabled="!bhpDataHdm"
                                    >
                                        <template #label v-if="!bhpDataHdm && historyOrInjectionError">
                                            <div>
                                                <p class="ma-0">Из ГДМ</p>
                                                <p class="ma-0 text--disabled text--small">
                                                    {{historyOrInjectionError}}
                                                </p>
                                            </div>
                                        </template>
                                    </v-radio>
                                </v-radio-group>
                            </div>
                            <history v-model="bhpData" :wells="realWells">
                                <template #buttons="{rows, titles, setRows}">
                                    <v-btn class="mb-2 mr-2"
                                           color="primary"
                                           elevation="0"
                                           @click="fillHistory(rows, titles, setRows)"
                                    >Заполнить пустые данные</v-btn>
                                </template>
                            </history>
                        </v-sheet>
                    </template>
                </step>
            </v-tabs-items>
        </v-tabs>
        <v-dialog v-model="externalValidateShow"
                  width="75%"
                  @close="closeExternalValidate"
        >
            <v-card>
                <v-card-title>Результаты проверки</v-card-title>
                <v-card-text>
                    <v-expansion-panels accordion multiple>
                        <v-expansion-panel v-if="externalValidateGroups.error.length > 0">
                            <v-expansion-panel-header>
                                Ошибки
                                <template v-slot:actions>
                                    <v-chip color="error" dark small>{{externalValidateGroups.error.length}}</v-chip>
                                </template>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-expansion-panels accordion>
                                    <v-expansion-panel v-for="(error, index) in externalValidateGroups.error" :key="index">
                                        <v-expansion-panel-header>
                                            {{error.title}}
                                            <template v-slot:actions>
                                                <v-chip small  color="error" dark>{{error.items.length}}</v-chip>
                                            </template>
                                        </v-expansion-panel-header>
                                        <v-expansion-panel-content>
                                            <pre>{{error.items.join('\n')}}</pre>
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                        <v-expansion-panel v-if="externalValidateGroups.warning.length > 0">
                            <v-expansion-panel-header>
                                Предупреждения
                                <template v-slot:actions>
                                    <v-chip color="orange" dark small>{{externalValidateGroups.warning.length}}</v-chip>
                                </template>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-expansion-panels accordion>
                                    <v-expansion-panel v-for="(warning, index) in externalValidateGroups.warning" :key="index">
                                        <v-expansion-panel-header>
                                            {{warning.title}}
                                            <template v-slot:actions>
                                                <v-chip small  color="orange" dark>{{warning.items.length}}</v-chip>
                                            </template>
                                        </v-expansion-panel-header>
                                        <v-expansion-panel-content>
                                            <pre>{{warning.items.join('\n')}}</pre>
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                        <v-expansion-panel>
                            <v-expansion-panel-header>Полный лог</v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <pre>{{externalValidateResult}}</pre>
                            </v-expansion-panel-content>
                        </v-expansion-panel>

                    </v-expansion-panels>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="closeExternalValidate">Закрыть</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-footer>
            <v-btn color="primary"
                   text
                   :disabled="!isValid"
                   class="mb-4 mr-2"
                   @click="saveModelAndGoToCalc"
            >Сохранить и к адаптации</v-btn>
            <v-btn color="primary"
                   text
                   class="mb-4 mr-2"
                   @click="saveModelAndGoToList"
            >Сохранить и к списку</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary"
                   :disabled="!isValid"
                   text
                   class="mb-4 mr-4"
                   @click="downloadJson"
            >Скачать JSON</v-btn>
            <v-btn color="primary"
                   :disabled="!isValid"
                   text
                   class="mb-4 mr-4"
                   @click="externalValidate"
            >Проверить расчет</v-btn>
            <v-btn color="primary"
                   :loading="saving"
                   large
                   class="mb-4"
                   @click="save"
            >Сохранить модель</v-btn>
        </v-footer>
    </v-form>
</template>

<script>
import Step from "@/components/Problem/Steps/Step";
import Ofp from "@/components/Problem/Steps/Ofp";
import Wells from "@/components/Problem/Wells";
import {TYPE_IMAGINARY, Well, Node} from "@/Well";
import History from "@/components/Problem/History";
import Grid from "@/components/Grid/Grid";
import {
    parseCsvFile, padArray, arrayToFloat, arrayToInt, arrayBufferToString, setWellXYZ, downloadJson,
    findNodeIndex, getWellConnections, extractZipFile, blobToFile, getFileContent
} from "@/utils";
import clone from "lodash.clonedeep";
import axios from "axios";

import {TYPE_PRODUCE, TYPE_INJECT, TYPE_CLOSED} from "@/utils";

function isEmpty(array) {
    if (array?.length > 0) {
        return false;
    }

    return true;
}

export default {
    name: "NewDataStepsForm",
    props: ['value', 'oneStep', 'editState'],
    components: {Step, Wells, History, Grid, Ofp},
    data() {
        let inputConstants = Object.assign({}, this.$store.state.grid.inputConstants);
        let ofpConstants = Object.assign({}, this.$store.state.grid.ofpConstants);
        let item = this.value ? clone(this.value) : {};

        return {
            item,
            tab: 'upload',
            uploadType: 'file',
            loading: true,
            itemsLoading: false,
            showCopyWellsMenu: false,
            externalValidateShow: false,
            initializing: false,
            connectionsLoading: false,
            saving: false,
            unsavedFound: false,
            unsaved: null,
            gridFiles: null,
            shapeFiles: null,
            rockFiles: null,
            fluidFiles: null,
            wellFiles: null,
            swsoFiles: null,
            injectionData: null,
            injectionDataCsv: null,
            injectionDataFile: null,
            injectionDataError: null,
            injectionDataType: 'csv',
            historyData: null,
            historyDataCsv: null,
            historyDataFile: null,
            historyDataError: null,
            historyDataType: 'csv',
            bhpData: null,
            bhpDataCsv: null,
            bhpDataFile: null,
            bhpDataError: null,
            bhpDataType: 'csv',
            oneStepFiles: null,
            pFiles: null,
            grid: null,
            gridMeshFile: null,
            gridSize: [1, 1, 1],
            gridCellSize: [1, 1, 1],
            depthType: null,
            waitSaveIntervalId: null,
            inputConstants,
            loadedConstantsUpdated: 0,
            ignoreSizeUpdates: 0,
            wells: [],
            connectionsError: null,
            editConnections: [],
            editNodes: [],
            isParsed: false,
            connectionsMethod: null,
            ofpConstants,
            stepWaitValidateInputs: {
                'grid': 'input_grid_size_x',
                'swso': 'input_sw',
                'p': 'p',
                'fluid': 'input_mu_w',
                'rock': 'input_cr',
            },
            validationRules: {
                zeroToOne: [
                    v => !!v || 'Обязательное поле',
                    v => parseFloat(v) > 0 && parseFloat(v) <= 1 || 'Допустимые значения от 0 (не включая) до 1'
                ],
                gtZero: [
                    v => !!v || 'Обязательное поле',
                    v => parseFloat(v) > 0 || 'Значение должно быть больше 0'
                ]
            },
            excludeFromState: [
                'excludeFromState', 'validationRules', 'tab', 'unsaved', 'unsavedFound',
                'loading', 'connectionsLoading', 'item', 'initializing', 'injectionDataFile',
                'historyDataFile', 'bhpDataFile', 'gridMeshFile', 'saving', 'loading',
                'oneStepFiles', 'externalValidateResult', 'injectionDataType',
                'historyDataType', 'bhpDataType', 'allFilesText', 'connectionsErrors', 'connectionsError'
            ],
            externalValidateResult: null,
            activeWellId: null,
            injectionDataHdm: null,
            historyDataHdm: null,
            bhpDataHdm: null,
            allFilesText: null,
        }
    },
    watch: {
        async value() {
            await this.initProblem();
        },
        gridProperties: {
            deep: true,
            handler() {
                if (!this.gridProperties) {
                    return;
                }

                if (this.gridProperties.size) {
                    this.ignoreSizeUpdates += 1;
                    this.gridSize = this.gridProperties.size;
                }

                if (this.gridProperties.cell_size) {
                    this.ignoreSizeUpdates += 1;
                    this.gridCellSize = this.gridProperties.cell_size;
                }
            }
        },
        gridFiles: {
            deep: true,
            handler() {
                this.updateGrid('size');
            }
        },
        gridSize: {
            deep: true,
            handler() {
                if (this.ignoreSizeUpdates > 0) {
                    this.ignoreSizeUpdates -= 1;
                    return;
                }

                this.updateGrid('size');
            }
        },
        gridCellSize: {
            deep: true,
            handler() {
                if (this.ignoreSizeUpdates > 0) {
                    this.ignoreSizeUpdates -= 1;
                    return;
                }

                this.updateGrid('size');
            }
        },
        parsedWells: {
            deep: true,
            handler() {
                this.setWells(this.parsedWells);
            }
        },
        parsedInjectionDataHdm: {
            deep: true,
            handler() {
                this.injectionDataHdm = this.parsedInjectionDataHdm;
            }
        },
        parsedHistoryDataHdm: {
            deep: true,
            handler() {
                this.historyDataHdm = this.parsedHistoryDataHdm;
            }
        },
        parsedBhpDataHdm: {
            deep: true,
            handler() {
                this.bhpDataHdm = this.parsedBhpDataHdm;
            }
        },
        wells: {
            deep: true,
            async handler() {
                this.updateEditNodes();
                await this.updateConnections();
            }
        },
        injectionDataType() {
            if (this.injectionDataType === 'csv') {
                this.injectionData = this.injectionDataCsv;
            }
            else {
                this.injectionData = this.injectionDataHdm;
            }
        },
        injectionData() {
            this.updateWellRates();
        },
        historyDataType() {
            if (this.historyDataType === 'csv') {
                this.historyData = this.historyDataCsv;
            }
            else {
                this.historyData = this.historyDataHdm;
            }
        },
        bhpData() {
            this.updateWellPressures();
        },
        bhpDataType() {
            if (this.bhpDataType === 'csv') {
                this.bhpData = this.bhpDataCsv;
            }
            else {
                this.bhpData = this.bhpDataHdm;
            }
        },
        injectionDataFile() {
            this.updateInjectionData();
        },
        historyDataFile() {
            this.updateHistoryData();
        },
        bhpDataFile() {
            this.updateBhpData();
        },
        loadedConstants: {
            deep: true,
            handler () {
                this.loadedConstantsUpdated++;
                let loadedConstants = Object
                    .keys(this.loadedConstants)
                    .filter(constant => {
                        return this.loadedConstants[constant] !== null;
                    });

                for (let constant of loadedConstants) {
                    if (!this.inputConstants[constant]) {
                        this.inputConstants[constant] = this.loadedConstants[constant];
                    }
                }
            }
        },
        allConstants() {
            this.updateWellConstants();
        },
        async allFiles() {
            if (this.allFiles) {
                await this.updateAllFilesText();
            }
            else {
                this.allFilesText = null;
            }
        },
        async tab() {
            if (typeof (this.stepWaitValidateInputs[this.tab]) !== "undefined") {
                let refName = this.stepWaitValidateInputs[this.tab];
                await this.waitForRefAndValidate(refName);
            }
            if (this.tab !== this.inputTab) {
                this.$router.push({path: this.$route.path, query: {tab: this.tab}});
            }
        },
        state: {
            deep: true,
            async handler() {
                await this.saveUnsaved();
            }
        },
        async showCopyWellsMenu() {
            await this.loadItems();
        }
    },
    async created() {
        this.tab = this.inputTab;
        this.$store.commit('grid/resetAllErrors');
        await this.initProblem();
    },
    methods: {
        async updateAllFilesText() {
            this.allFilesText = await this.getFilesText(this.allFiles);
        },
        async loadItems() {
            this.itemsLoading = true;
            await this.$store.dispatch('problem/loadItems');
            this.itemsLoading = false;
        },
        getColumn(title, rows, defaultValue=null) {
            let column = [];
            for (let row of rows) {
                column.push(row[title] || defaultValue);
            }

            return column;
        },
        fillHistory(rows, titles, setRows, injection) {
            if (injection) {
                titles = this.realWells.map(well => well.name);
            }

            for (let title of titles) {
                if (title === 't') {
                    continue;
                }

                let values = this.getColumn(title, rows);
                for (let valueIndex = 0; valueIndex < values.length; valueIndex++) {
                    let value = parseFloat(values[valueIndex]) || 0.0;
                    if (value === 0.0) {
                        rows[valueIndex][title] = value;
                    }
                }
            }

            if (typeof (setRows) === 'function') {
                setRows(rows);
            }

            return rows;
        },
        interpolateBhp(rows, titles, setRows) {
            let t = this.getColumn('t', rows);
            let baseBhp = this.inputConstants['p'];

            for (let title of titles) {
                if (title === 't') {
                    continue;
                }

                let values = this.getColumn(title, rows);
                let gapsToFill = [];
                let fromIndex = null;
                let toIndex = null;

                for (let valueIndex = 0; valueIndex < values.length; valueIndex++) {
                    let value = parseFloat(values[valueIndex]) || 0.0;
                    if (value === 0) {
                        if (fromIndex === null) {
                            fromIndex = valueIndex;
                        }
                        toIndex = valueIndex;
                    }

                    if (value !== 0 || valueIndex === values.length - 1) {
                        if (toIndex !== null) {
                            gapsToFill.push([fromIndex, toIndex]);
                            fromIndex = null;
                            toIndex = null;
                        }
                    }
                }

                for (let gap of gapsToFill) {
                    fromIndex = gap[0];
                    toIndex = gap[1];

                    let fillBhp = null;
                    if (fromIndex === 0) {
                        fillBhp = baseBhp;
                    }
                    else if (toIndex === values.length-1) {
                        fillBhp = values[fromIndex-1];
                    }

                    let startBhp = null;
                    let endBhp = null;
                    let startTime = null;
                    let endTime = null;

                    if (fillBhp === null) {
                        startBhp = parseFloat(values[fromIndex-1]);
                        endBhp = parseFloat(values[toIndex+1]);
                        startTime = parseFloat(t[fromIndex-1]);
                        endTime = parseFloat(t[toIndex+1]);
                    }

                    for (let i = fromIndex; i <= toIndex; i++) {
                        let currentBhp = fillBhp;
                        if (currentBhp === null) {
                            let bhpPerDay = startTime !== endTime
                                ? (endBhp - startBhp)/(endTime-startTime)
                                : 0;

                            let currentTime = parseFloat(t[i]);
                            currentBhp = startBhp + bhpPerDay * (currentTime - startTime);
                        }

                        rows[i][title] = parseFloat(currentBhp.toFixed(2));
                    }
                }
            }

            if (typeof (setRows) === 'function') {
                setRows(rows);
            }

            return rows;
        },
        async setOneStepFiles(newFiles) {
            let dataFiles = [];
            let unzippedFiles = [];
            for (let file of newFiles) {
                let ext = file?.name?.split('.')[1];
                if (ext) {
                    ext = ext.toLowerCase();
                }

                if (ext === 'zip') {
                    let unzipped = await extractZipFile(file);
                    let files = Array.from(unzipped).map(([filename, blob]) => blobToFile(blob, filename));
                    unzippedFiles = unzippedFiles.concat(files);
                }
                else {
                    unzippedFiles.push(file);
                }
            }

            for (let file of unzippedFiles) {
                let ext = file?.name?.split('.')[1];
                if (ext) {
                    ext = ext.toLowerCase();
                }

                if (ext === 'csv') {
                    if (file?.name?.toLowerCase().includes('bhp')) {
                        this.bhpDataFile = [file];
                    }

                    if (file?.name?.toLowerCase().includes('liq')) {
                        this.injectionDataFile = [file];
                    }

                    if (file?.name?.toLowerCase().includes('oil')) {
                        this.historyDataFile = [file];
                    }
                }
                else if (ext === 'json') {
                    continue;
                }
                else {
                    dataFiles.push(file);
                }
            }

            this.oneStepFiles = dataFiles;
            this.connectionsAutoload = true;
            this.$nextTick(async () => {
                await this.updateAllFilesText();
                await this.updateGrid('oneStep');
            });
        },
        async updateGrid(target) {
            if (this.initializing) {
                return;
            }

            if (!this.allFilesText) {
                return;
            }

            let cellSize = null;
            let cellCount = null;

            if (this.gridCellSize && this.gridCellSize.length > 0) {
                cellSize = padArray(arrayToFloat(this.gridCellSize), 3);
            }

            if (this.gridSize && this.gridSize.length > 0) {
                cellCount = padArray(arrayToInt(this.gridSize), 3);
            }

            await this.$store.commit('grid/setProcessing', true);
            await this.$store.dispatch('grid/updateGrid', {
                target,
                actions: this.allActions,
                grid_properties: this.gridProperties,
                cell_size: cellSize,
                cell_count: cellCount,
                file: this.allFilesText,
                files: await this.getFilesObject(this.allFiles),
                problem_id: this.item?.id || null,
            });
            await this.loadGridMesh();
            await this.updateConnections();
            await this.$store.commit('grid/setProcessing', false);
            this.isParsed = Boolean(this.allFilesText);
            this.unsavedFound = false;
            this.$nextTick(this.validate);
        },
        async updateWells() {
            await this.$store.dispatch('grid/updateWells', {
                grid_properties: this.gridProperties,
                file: this.allFilesText,
            });
        },
        copyWells(wc, item) {
            wc.copyWellsFrom(item);
            this.showCopyWellsMenu = false;
        },
        async updateConstants(target) {
            await this.$store.dispatch('grid/updateConstants', {
                target,
                constants: this.constants,
                file: this.allFilesText,
            });
        },
        async updateInjectionData() {
            let wellsData = await this.uploadCsvFile(this.injectionDataFile);
            let wellsAsFloat = this.convertCsvToFloats(wellsData);
            this.injectionData = wellsAsFloat;
            this.injectionDataCsv = wellsAsFloat;
        },
        async updateHistoryData() {
            let wellsData = await this.uploadCsvFile(this.historyDataFile);
            let wellsAsFloat = this.convertCsvToFloats(wellsData);
            this.historyData = wellsAsFloat;
            this.historyDataCsv = wellsAsFloat;
        },
        updateWellConstants() {
            this.wells = this.jsonWells.map(wellData => new Well(wellData, this.gridProperties, this.allConstants));
        },
        updateWellRates() {
            if (this.injectionData) {
                for (let wellId in this.injectionData) {
                    if (wellId === 't') {
                        continue;
                    }

                    let well = this.wells.find(well => well.id === wellId);
                    let rate = this.injectionData[wellId][0] || this.injectionData[wellId][1] || null;
                    if (rate === null && this.injectionData[wellId][0] === 0 && this.injectionData[wellId][1] === 0) {
                        rate = 0;
                    }

                    if (well && typeof (rate) === 'number') {
                        well.q_initial_rate = rate;
                    }
                }
            }
        },
        async updateBhpData() {
            let wellsData = await this.uploadCsvFile(this.bhpDataFile);
            let wellsAsFloat = this.convertCsvToFloats(wellsData);
            this.bhpData = wellsAsFloat;
            this.bhpDataCsv = wellsAsFloat;
        },
        updateWellPressures() {
            if (this.bhpData) {
                for (let wellId in this.injectionData) {
                    if (wellId === 't') {
                        continue;
                    }

                    let well = this.wells.find(well => well.id === wellId);
                    let bhp = this.bhpData[wellId]
                        ? this.bhpData[wellId][0] || null
                        : null;

                    if (well && bhp > 0) {
                        well.p_pressure = bhp;
                    }
                }
            }
        },
        setConnections(newConnections) {
            debugger;
            this.editConnections = newConnections;
        },
        setConnectionsError(error) {
            this.connectionsError = error
        },
        updateEditNodes() {
            if (!this.wells) {
                this.editNodes = [];
                return;
            }

            this.editNodes = this.wells.reduce((nodes, well) => {
                if (well?.nodes) {
                    nodes = nodes.concat(well.nodes);
                }

                return nodes;
            }, []);
        },
        updateConnectionsFromWells() {
            if (!this.wells || (this.wells && this.wells.length === 0)) {
                return;
            }

        },
        async updateConnections(method = null, manual = false) {
            if (!this.connectionsAutoload && !manual) {
                return this.updateConnectionsFromWells();
            }

            if (this.connectionsLoading) {
                return;
            }

            if (!this.wells || (this.wells && this.wells.length === 0)) {
                return;
            }

            if (method) {
                this.connectionsMethod = method;
            }

            this.editConnections = [];
            this.connectionsLoading = true;
            let result = await this.$store.dispatch('grid/updateConnections', {
                method: this.connectionsMethod,
                problem: this.unsavedProblem,
                wells: this.jsonWells
            });
            this.connectionsLoading = false;

            if (!result) {
                return;
            }

            let {connections, nodes} = result;
            //Сначала обновить ноды, потому что событие перерисовки стоит на
            //обновлении соединений. Получается, что оно срабатывает когда меняются
            //соединения, а ноды не успевают
            if (nodes) {
                this.editNodes = nodes.map(nodeData => {
                    let well = this.wells.find(well => well.id === nodeData.well_id);
                    return new Node(nodeData, well, this.gridProperties);
                });
            }

            if (connections) {
                this.editConnections = connections;
            }
        },
        convertCsvToFloats(uploadedCsvData) {
            for (let columnTitle in uploadedCsvData) {
                let column = uploadedCsvData[columnTitle];
                uploadedCsvData[columnTitle] = column.map(str => {
                    let val = 0;
                    try {
                        val = parseFloat(str);
                        if (isNaN(val)) {
                            val = 0;
                        }
                    } catch (e) {
                        val = 0;
                    }

                    return val;
                });
            }

            return uploadedCsvData;
        },
        async uploadCsvFile(csvFile) {
            if (csvFile) {
                let injectionDataCsv = await this.getFilesText(csvFile);
                let injectionData = parseCsvFile(injectionDataCsv, ',');
                let wrongSeparator = Boolean(injectionData && injectionData[0]) && injectionData[0].length === 1;
                if (wrongSeparator) {
                    injectionData = parseCsvFile(injectionDataCsv, ';');
                }

                let header = injectionData[0];
                let rows = injectionData.slice(1);

                let wellsData = header.reduce((data, colName) => {
                    data[colName] = [];
                    return data;
                }, {});

                for (let rowIndex in rows) {
                    let row = rows[rowIndex];
                    for (let colIndex in header) {
                        let colName = header[colIndex];
                        let colValue = row[colIndex];
                        wellsData[colName][rowIndex] = colValue;
                    }
                }

                return wellsData;
            }

            return null;
        },
        async getFilesText(files) {
            if (!files) {
                return null;
            }

            let contents = [];
            for (let file of files) {
                contents.push( await getFileContent(file) );
            }

            return contents.join('\n\n\n');
        },
        async getFilesObject(files) {
            if (!files) {
                return null;
            }

            let filesObject = {}
            for (let file of files) {
                filesObject[file.name] = await getFileContent(file);
            }

            return filesObject;
        },
        isConstantSet(constant) {
            return Boolean(this.inputConstants) &&
                typeof (this.inputConstants[constant]) !== 'undefined' &&
                this.inputConstants[constant] !== null;
        },
        isConstantSetAndNotZero(constant) {
            return this.isConstantSet(constant) && parseFloat(this.inputConstants[constant]) > 0;
        },
        getConnectionMatrix() {
            let nodesCount = this.editNodes?.length || 0;
            if (!nodesCount) {
                return null;
            }

            if (!this.editConnections) {
                return null;
            }

            let matrix = Array(nodesCount)
                .fill(null)
                .map(() => Array(nodesCount).fill(0));

            for (let connection of this.editConnections) {
                let [indexA, indexB] = connection;
                if (matrix[indexA]) {
                    matrix[indexA][indexB] = 1;
                }

                if (matrix[indexB]) {
                    matrix[indexB][indexA] = 1;
                }
            }

            return matrix.map(row => row.join(''));
        },
        getWellEvents(wells=null) {
            if (!wells) {
                wells = this.wells;
            }

            let events = {};
            if (this.joinedInjectionHistoryData) {
                let wellIds = Object.keys(this.joinedInjectionHistoryData);
                for (let wellId of wellIds) {
                    let wellIndex = wells.findIndex(well => well.id == wellId);
                    if (wellIndex === -1) {
                        continue;
                    }

                    let well = wells[wellIndex];
                    let oldType = parseInt(well.type);
                    for (let injectIndex in this.joinedInjectionHistoryData[wellId]) {
                        injectIndex = parseInt(injectIndex);
                        let prevIndex = injectIndex - 1;

                        let inject = this.joinedInjectionHistoryData[wellId][injectIndex];
                        let prevInject = typeof (this.joinedInjectionHistoryData[wellId][prevIndex]) !== 'undefined'
                            ? this.joinedInjectionHistoryData[wellId][prevIndex]
                            : null;

                        if (prevInject === null) {
                            continue;
                        }

                        let newType = null;

                        let changedSign = prevInject * inject < 0;
                        let stopped = prevInject !== 0 && inject === 0;
                        let started = prevInject === 0 && inject !== 0;

                        if (changedSign) {
                            newType = inject > 0 ? TYPE_INJECT : TYPE_PRODUCE;
                        }

                        if (stopped) {
                            newType = TYPE_CLOSED;
                        }

                        if (started) {
                            newType = inject > 0 ? TYPE_INJECT : TYPE_PRODUCE;
                        }

                        if (newType && newType !== oldType) {
                            if (typeof (events[injectIndex]) === 'undefined') {
                                events[injectIndex] = {
                                    time_step: injectIndex,
                                    changed_wells: [wellIndex],
                                    new_types: [newType],
                                    old_types: [oldType],
                                }
                            } else {
                                events[injectIndex]['changed_wells'].push(wellIndex);
                                events[injectIndex]['new_types'].push(newType);
                                events[injectIndex]['old_types'].push(oldType);
                            }

                            oldType = newType;
                        }

                        prevInject = inject;
                    }
                }
            }

            return Object.values(events);
        },
        makeProblemFromLoadedData() {
            let problemName = this.item?.name;
            let [cellsX, cellsY, cellsZ] = this.gridSize;
            let [dx, dy, dz] = this.gridCellSize;

            let phi = this.inputConstants['phi'];
            let k = this.inputConstants['k'];

            if (this.porosityDefinedInCells) {
                phi = this.meanCellPorosity;
                k = this.meanCellPermeability;
            }

            let datumDepth = parseFloat(this.inputConstants['dd']);
            let rhoW = parseFloat(this.inputConstants['rho_w']);
            let rhoO = parseFloat(this.inputConstants['rho_o']);
            let siw = parseFloat(this.inputConstants['sw']);
            let sor = parseFloat(this.inputConstants['so']);

            let nodesCount = this.wells.reduce((count, well) => count += well.nodes ? well.nodes.length : 0, 0);

            let oldItem = this.item || {};
            let historyWithoutT = [];
            if (this.historyData) {
                historyWithoutT = Object.keys(this.historyData).reduce((history, colName) => {
                    if (colName !== 't') {
                        history[colName] = this.historyData[colName];
                    }

                    return history;
                }, {});
            }

            let bhpWithoutT = [];
            if (this.bhpData) {
                bhpWithoutT = Object.keys(this.bhpData).reduce((history, colName) => {
                    if (colName !== 't') {
                        history[colName] = this.bhpData[colName];
                    }

                    return history;
                }, {});
            }

            let correctedWells = [];
            for (let jsonWellSrc of this.jsonWells) {
                let jsonWell = Object.assign({}, jsonWellSrc);
                let wellId = jsonWell['id'];
                let injection = this.injectionData ? this.injectionData[wellId] || null : null;
                let production = this.historyData ? this.historyData[wellId] || null : null;
                let isShut = !(injection > 0) && !(production > 0);
                let isImaginary = jsonWell['type'] === TYPE_IMAGINARY;

                if (isShut && !isImaginary) {
                    jsonWell['type'] = TYPE_CLOSED;
                }

                if (jsonWell.nodes && jsonWell.nodes.length > 0) {
                    delete jsonWell['phi_well_porosity'];
                    delete jsonWell['k_well_permeability'];
                    delete jsonWell['h_well_net_thickness'];
                    delete jsonWell['p_pressure'];
                    delete jsonWell['sw_water_saturation'];
                    delete jsonWell['x_coord'];
                    delete jsonWell['y_coord'];
                    delete jsonWell['z_coord'];

                    jsonWell.nodes = jsonWell.nodes.map(node => {
                        delete node['bottomhole_depth'];
                        return node;
                    });
                }

                correctedWells.push(jsonWell);
            }

            let newItem = {
                "name": problemName || "",
                "type": oldItem?.type || "base",
                "reservoir": {
                    "grids_x": cellsX,
                    "grids_y": cellsY,
                    "grids_z": cellsZ,
                    "dx": dx,
                    "dy": dy,
                    "dz": dz,
                },
                "depth_index": oldItem?.depth_index || 2,
                "total_pore_volume": this.gridVolume * phi,
                "total_nodes": nodesCount,
                "mu_w_water_viscosity": parseFloat(this.inputConstants['mu_w']),
                "mu_o_oil_viscosity": parseFloat(this.inputConstants['mu_o']),
                "cr_rock_compressibility": parseFloat(this.inputConstants['cr']),
                "co_oil_compressibility": parseFloat(this.inputConstants['co']),
                "cw_water_compressibility": parseFloat(this.inputConstants['cw']),
                "k_mean_permeability": k,
                "phi_mean_porosity": phi,
                "s_iw_irreducible_water_saturation": siw,
                "s_or_residual_oil_saturation": sor,
                "p_mean_reservoir_pressure": parseFloat(this.inputConstants['p']),
                "d_datum_depth": datumDepth,
                "rhoo_mean_density_oil": rhoO,
                "rhow_mean_density_water": rhoW,
                "rhoo_density_oil_sc": parseFloat(this.inputConstants['rho_o_sc']),
                "rhow_density_water_sc": parseFloat(this.inputConstants['rho_w_sc']),
                "wells": correctedWells,
                "schedule_data": {
                    "t": this.injectionData ? this.injectionData['t'] || [] : [],
                    ...this.joinedInjectionHistoryData,
                    "events": this.getWellEvents(correctedWells),
                },
                "oil_history_data": historyWithoutT,
                "bhp_data": bhpWithoutT,
                "history_error": oldItem['history_error'],
                "uploadedFiles": this.allFilesText ? this.allFilesText : oldItem['uploadedFiles'] || "",
                "a_relative_permeability": this.ofpConstants['a'],
                "no_relative_permeability": this.ofpConstants['no'],
                "nw_relative_permeability": this.ofpConstants['nw'],
                "connection_matrix": this.getConnectionMatrix(),
                "units": this.$store.state.grid.units
            };

            if (oldItem?._id) {
                newItem['_id'] = oldItem._id;
            }

            if (oldItem?.id) {
                newItem['id'] = oldItem.id;
            }

            if (oldItem?.v_aquifer_volume) {
                newItem['v_aquifer_volume'] = oldItem.v_aquifer_volume;
                newItem['c_aquifer_compressibility'] = oldItem.c_aquifer_compressibility;
                newItem['p_aquifer_porosity'] = oldItem.p_aquifer_porosity;
            }

            return newItem;
        },
        async loadGridMesh() {
            if (!this.item?.id) {
                return;
            }

            this.gridMeshFile = await this.$store.dispatch('grid/loadGridMesh', {
                problemId: this.item.id,
                problem: this.item,
                size: this.gridSize,
                cellSize: this.gridCellSize,
                isParsed: this.isParsed,
            });

            await this.$store.dispatch('grid/loadGridIndexes', this.item.id);
        },
        async initProblem(problem = null) {
            if (this.initializing) {
                return;
            }

            this.initializing = true;

            if (problem === null) {
                problem = this.value ? clone(this.value) : {}
            }

            this.item = problem;
            this.isParsed = this.item?.reservoir?.grids_x > 0 &&
                this.item?.reservoir?.grids_y > 0 &&
                this.item?.reservoir?.grids_z > 0;

            this.$store.commit('grid/resetAllErrors');
            if (this.isParsed) {
                this.updateConstantsFromProblem();
                await this.loadGridMesh();
            }

            if (this.editState && typeof (this.editState) === 'object' && Object.keys(this.editState).length > 0) {
                await this.loadState(this.editState);
                //this.unsavedFound = false;
            } else if (this.item?.id) {
                try {
                    let response = await axios.get('/api/problem/getState', {params: {id: this.item?.id}});
                    this.unsaved = response?.data || {};
                    delete this.unsaved['_id'];
                    this.unsavedFound = Boolean(this.unsaved) &&
                        Object.keys(this.unsaved).length > 0 &&
                        JSON.stringify(this.unsaved) !== JSON.stringify(this.state);
                } catch (e) {
                    this.unsavedFound = false;
                    this.unsaved = null;
                }
            } else {
                this.unsavedFound = false;
            }

            this.connectionsAutoload = false;
            this.$nextTick(() => {
                this.loading = false;
                this.initializing = false;
            });
        },
        async saveUnsaved() {
            if (!this.item?.id) {
                return;
            }

            if (this.initializing) {
                return;
            }

            if (this.unsavedFound) {
                return;
            }

            try {
                await this.$store.dispatch('problem/saveState', this.state);
            } catch (e) {
            }
        },
        async loadState(editState) {
            this.connectionsLoading = true;
            for (let key in editState) {
                if (typeof (this[key]) !== 'undefined') {
                    if (['wells', 'gridProperties', 'allConstants'].includes(key)) {
                        continue;
                    }

                    if (this.excludeFromState.includes(key)) {
                        continue;
                    }

                    if (key === 'oneStepFiles') {
                        this.oneStepFiles = [];
                        for (let fileName in editState['oneStepFiles']) {
                            this.oneStepFiles.push({
                                name: fileName,
                                content: editState['oneStepFiles'][fileName],
                            });
                        }
                    }
                    else {
                        this[key] = editState[key];
                    }
                }
            }

            this.$store.dispatch('grid/setGridData', editState.gridProperties, editState.allConstants);
            this.setWells(editState['wells']);
            await this.loadGridMesh();
            this.connectionsLoading = false;
        },
        async loadUnsaved() {
            if (!this.unsavedFound) {
                return;
            }

            if (this.unsaved) {
                await this.loadState(this.unsaved);
                this.unsaved = null;
                this.unsavedFound = false;
            }
        },
        setWells(newWells) {
            if (!newWells) {
                this.wells = [];
                return;
            }

            let gridData = this.gridProperties;
            this.wells = newWells.map(wellData => new Well(wellData, gridData, this.allConstants));
            this.updateWellRates();
        },
        editWell(well) {
            if (!this.$refs.wells) {
                return;
            }

            this.$refs.wells.editWell(well);
        },
        updateConstantsFromProblem() {
            if (!this.item) {
                return;
            }

            if (!this.item?.reservoir) {
                return;
            }
            let gridProperties = Object.assign(this.gridProperties || {}, {
                size: this.gridSize,
                cell_size: this.gridCellSize
            });

            this.$store.commit('grid/setGridProperties', gridProperties);

            this.inputConstants['mu_w'] = this.item["mu_w_water_viscosity"] || 0;
            this.inputConstants['mu_o'] = this.item["mu_o_oil_viscosity"] || 0;
            this.inputConstants['cr'] = this.item["cr_rock_compressibility"] || 0;
            this.inputConstants['co'] = this.item["co_oil_compressibility"] || 0;
            this.inputConstants['cw'] = this.item["cw_water_compressibility"] || 0;
            this.inputConstants['phi'] = this.item["phi_porosity"] || this.item["phi_mean_porosity"] || 0;
            this.inputConstants['k'] = this.item["k_permeability"] || this.item["k_mean_permeability"] || 0;
            this.inputConstants['sw'] = this.item["s_iw_irreducible_water_saturation"] || 0;
            this.inputConstants['so'] = this.item["s_or_residual_oil_saturation"] || 0;
            this.inputConstants['p'] = this.item["p_reservoir_pressure"] || this.item["p_mean_reservoir_pressure"] || 0;
            this.inputConstants['dd'] = this.item["d_datum_depth"] || 0;

            this.inputConstants['rho_o'] = this.item["rhoo_mean_density_oil"] || 0;
            this.inputConstants['rho_w'] = this.item["rhow_mean_density_water"] || 0;
            this.inputConstants['rho_o_sc'] = this.item["rhoo_density_oil_sc"] || 0;
            this.inputConstants['rho_w_sc'] = this.item["rhow_density_water_sc"] || 0;

            this.gridSize = [this.item.reservoir.grids_x || 0, this.item.reservoir.grids_y || 0, this.item.reservoir.grids_z || 0];
            this.gridCellSize = [this.item.reservoir.dx || 0, this.item.reservoir.dy || 0, this.item.reservoir.dz || 0];
            this.setWells(this.item.wells);
            this.injectionData = this.item.schedule_data;
            if (this.injectionData?.events) {
                delete this.injectionData.events;
            }

            this.historyData = this.item.oil_history_data;
            this.bhpData = this.item.bhp_data;
            this.historyData['t'] = this.injectionData['t'];
            this.bhpData['t'] = this.injectionData['t'];

            this.ofpConstants['a'] = this.item["a_relative_permeability"];
            this.ofpConstants['no'] = this.item["no_relative_permeability"];
            this.ofpConstants['nw'] = this.item["nw_relative_permeability"];
        },
        waitProblemSaved() {
            return new Promise((resolve, reject) => {
                let msPassed = 0;
                this.waitSaveIntervalId = setInterval(() => {
                    let savedModel = this.$store.state.problem.edit;
                    if (savedModel && savedModel.id) {
                        clearInterval(this.waitSaveIntervalId);
                        return resolve(savedModel);
                    }
                    msPassed += 10;
                    if (msPassed > 15000) {
                        clearInterval(this.waitSaveIntervalId);
                        return reject('next_stepПревышен интервал ожидания ответа');
                    }
                }, 10);
            });
        },
        async save() {
            this.saving = true;
            let problem = this.makeProblemFromLoadedData();
            let state = this.state;
            let savedProblem = null;
            try {
                if (problem.id) {
                    await this.$store.dispatch('problem/saveItem', {id: problem.id, problem, state});
                } else {
                    await this.$store.dispatch('problem/newItem', {problem, state});
                }
                savedProblem = await this.waitProblemSaved();
            } catch (e) {
                this.$store.commit('setErrorMessage', 'Ошибка сохранения модели: ' + e.toString());
            }
            this.saving = false;
            return savedProblem;
        },
        async saveModelAndGoToCalc() {
            let savedProblem = await this.save();
            if (savedProblem) {
                this.$emit('next_step', savedProblem);
            }
        },
        async saveModelAndGoToList() {
            let savedProblem = await this.save();
            if (savedProblem) {
                this.$emit('list', savedProblem);
            }
        },
        waitForRefAndValidate(refKey) {
            return new Promise(resolve => {
                let interval;
                let msPassed = 0
                let msWait = 10;
                let msLimit = 1000;
                interval = setInterval(() => {
                    msPassed += msWait;
                    if (typeof (this.$refs[refKey]) !== 'undefined' || msPassed > msLimit) {
                        clearInterval(interval);
                        this.validate();
                        resolve();
                    }
                }, msWait);
            });
        },
        validate() {
            for (let inputName of Object.keys(this.$refs)) {
                if (!inputName.includes('input_')) {
                    continue;
                }

                let inputEl = this.$refs[inputName]?.$refs.input;
                if (inputEl) {
                    this.$refs[inputName]?.validate();
                    this.$refs[inputName]?.onInput({target: inputEl});
                }
            }
        },
        findClosestWell(coords) {
            let closestWell = null;
            let minDistance = null;

            for (let well of this.wells) {
                let distance = Math.sqrt(Math.pow(coords[0], 2) + Math.pow(coords[1], 2) + Math.pow(coords[2], 2));
                if (minDistance === null || minDistance > distance) {
                    closestWell = well;
                    minDistance = distance;
                }
            }

            return closestWell;
        },
        getNextWellNumber(prefix) {
            let nextNumber = 0;
            for (let well of this.wells) {
                try {
                    if (well.id.indexOf(prefix) === 0) {
                        let wellNumber = parseInt(well.id.replace(/[^\d]+/, ''));
                        if (wellNumber > nextNumber) {
                            nextNumber = wellNumber;
                        }
                    }
                } catch (e) {
                }
            }

            nextNumber += 1;
            return prefix + nextNumber;
        },
        addWellByCoords(xyzCoords, imaginary = true) {
            let coords = [xyzCoords.x, xyzCoords.y, xyzCoords.z];
            let newWell = clone(this.defaultWell);
            let newNode = {};

            let closestWell = this.findClosestWell(coords);
            if (closestWell) {
                newWell = clone(closestWell.json());
                newNode = clone(closestWell.nodes[0].json() || {});
            }

            if (imaginary) {
                newWell.id = this.getNextWellNumber('IMG');
                newWell.name = newWell.id;
                newWell.type = TYPE_IMAGINARY;
            }
            else {
                newWell.id = this.getNextWellNumber('USR');
                newWell.name = newWell.id;
            }

            setWellXYZ(newNode, coords, this.gridProperties);
            setWellXYZ(newWell, coords, this.gridProperties);

            newWell.nodes = [newNode];
            this.wells.push(new Well(newWell, this.grid, this.inputConstants));
        },
        deleteWell(well, nodeIndex = null) {
            let wellIndex = this.wells.indexOf(well);
            if (nodeIndex === null || well.nodes.length === 1) {
                this.deleteWellConnections(wellIndex);
                this.deleteWellNodes(wellIndex);
                this.wells.splice(wellIndex, 1);
            }
            else {
                let globalNodeIndex = findNodeIndex(well.nodes[nodeIndex], well['id'], this.editNodes);
                this.deleteNodeConnections(globalNodeIndex);
                well.nodes.splice(nodeIndex, 1);
                this.editNodes.splice(globalNodeIndex, 1);
            }
        },
        async addConnection(fromWell, toWell, fromNodeIndex, toNodeIndex) {
            let fromListIndex = this.editNodes.findIndex(node => node.well_id === fromWell.id && node.index == fromNodeIndex);
            let toListIndex = this.editNodes.findIndex(node => node.well_id === toWell.id && node.index == toNodeIndex);
            if (fromListIndex > -1 && toListIndex > -1) {
                let existent = this.editConnections.find(connection => {
                    return (connection[0] === fromListIndex && connection[1] === toListIndex) ||
                        (connection[1] === fromListIndex && connection[0] === toListIndex);
                });

                if (existent) {
                    return;
                }

                this.editConnections.push([fromListIndex, toListIndex]);
                this.connectionsAutoload = false;
            }
        },
        deleteWellNodes(wellIndex) {
            let well = this.wells[wellIndex];
            let renumberedConnections = clone(this.editConnections);
            this.editNodes = this.editNodes.filter((node, nodeIndex) => {
                if (node.well_id === well['id']) {
                    renumberedConnections = renumberedConnections.map(connection => {
                        if (connection[0] >= nodeIndex) {
                            connection[0] -= 1;
                        }

                        if (connection[1] >= nodeIndex) {
                            connection[1] -= 1;
                        }

                        return connection;
                    });

                    return false;
                }

                return true;
            });

            this.editConnections = renumberedConnections;
        },
        deleteWellConnections(wellIndex) {
            let wellConnections = getWellConnections(this.wells[wellIndex], this.editConnections, this.editNodes);

            this.editConnections = this.editConnections
                .filter(connection => !wellConnections.includes(connection));
        },
        deleteNodeConnections(nodeIndex) {
            this.editConnections = this.editConnections
                .filter(connection => !connection.includes(nodeIndex))
                .map(connection => {
                    if (connection[0] >= nodeIndex) {
                        connection[0] -= 1;
                    }

                    if (connection[1] >= nodeIndex) {
                        connection[1] -= 1;
                    }

                    return connection;
                });
        },
        deleteConnection(connectionIndex) {
            this.editConnections.splice(connectionIndex, 1);
            this.connectionsAutoload = false;
        },
        downloadJson() {
            downloadJson(this.makeProblemFromLoadedData());
        },
        async externalValidate() {
            let result = await this.$store.dispatch('problem/validate', this.makeProblemFromLoadedData());
            if (result?.result) {
                this.externalValidateResult = result?.result;
                this.externalValidateShow = true;
            }
            else {
                console.log(result);
            }
        },
        closeExternalValidate() {
            this.externalValidateShow = false;
            this.externalValidateResult = null;
        }
    },
    computed: {
        realWells() {
            if (!this.wells) {
                return [];
            }

            return this.wells.filter(well => !well.imaginary);
        },
        imaginaryWells() {
            if (!this.wells) {
                return [];
            }

            return this.wells.filter(well => well.imaginary);
        },
        jsonWells() {
            return this.wells.map(well => well.json());
        },
        gridProperties() {
            return this.$store.state.grid.gridProperties;
        },
        parsedWells() {
            return this.$store.state.grid.wells;
        },
        loadedConstants() {
            return this.$store.state.grid.constants;
        },
        gridHasData() {
            let gridNotEmpty = this.gridSize && this.gridCellSize && this.gridSize.length >= 3 && this.gridCellSize.length >= 3;
            let isDefaultGridSize = this.gridSize.join(',') === '0,0,0';
            let isDefaultGridCellSize = this.gridCellSize.join(',') === '0,0,0';
            let maxGridSize = Math.max(...this.gridSize);
            let maxCellSize = Math.max(...this.gridCellSize);

            let hasDefaultValues = isDefaultGridSize || isDefaultGridCellSize;
            return gridNotEmpty && !hasDefaultValues && maxGridSize > 0 && maxCellSize > 0;
        },
        anyError() {
            return this.oneStepFileError;
        },
        oneStepFileError() {
            return this.$store.state.grid.oneStepFileError;
        },
        parsedInjectionDataHdm() {
            return this.$store.state.grid.injectionData;
        },
        parsedHistoryDataHdm() {
            return this.$store.state.grid.historyData;
        },
        parsedBhpDataHdm() {
            return this.$store.state.grid.bhpData;
        },
        historyOrInjectionError() {
            return this.$store.state.grid.parsedProblem?.history_error;
        },
        allActions() {
            let actions = [];

            if (this.oneStepFiles) {
                actions = ['size', 'shape', 'rock', 'swso'];
            }

            if (this.gridFiles && !actions.includes('size')) {
                actions.push('size');
            }

            if (this.shapeFiles && !actions.includes('shape')) {
                actions.push('shape');
            }

            if (this.rockFiles && !actions.includes('rock')) {
                actions.push('rock');
            }

            if (this.swsoFiles && !actions.includes('swso')) {
                actions.push('swso');
            }

            return actions;
        },
        allFiles() {
            let files = this.oneStepFiles || [];

            if (files) {
                let uniqueFilesByName = files.filter((file, index, allFiles) => {
                    return allFiles.findIndex((searchFile) => searchFile.name === file.name) === index;
                });

                return uniqueFilesByName;
            }
            else {
                return null;
            }
        },
        constants() {
            let constants = [];
            if (this.pFiles) {
                constants = constants.concat(['p', 'dd']);
            }

            if (this.fluidFiles) {
                constants = constants.concat(['mu_w', 'mu_o', 'cw', 'co'])
                if (this.needDensity) {
                    constants = constants.concat(['rho_w', 'rho_w_sc', 'rho_o', 'rho_o_sc']);
                }
            }

            return constants;
        },
        porosityDefinedInCells() {
            return this.meanCellPorosity > 0;
        },
        permeabilityDefinedInCells() {
            return this.meanCellPermeability > 0;
        },
        meanCellPorosity() {
            this.loadedConstantsUpdated;
            return this.loadedConstants?.mean_porosity;
        },
        meanCellPermeability() {
            this.loadedConstantsUpdated;
            return this.loadedConstants?.mean_permeability;
        },
        gridErrors() {
            if (!this.gridHasData) {
                return ['Не указаны основные параметры сетки'];
            }

            return [];
        },
        isGridValid() {
            return isEmpty(this.gridErrors);
        },
        shapeErrors() {
            let errors = [];

            if (!this.allFiles) {
                errors.push('Не загружены файлы с формой залежи');
            }
            else if (this.oneStepFileError) {
                errors.push(this.oneStepFileError);
            }

            return errors;
        },
        isShapeValid() {
            return isEmpty(this.shapeErrors);
        },
        connectionsErrors() {
            let errors = [];
            if (this.connectionsError) {
                errors.push(this.connectionsError);
            }
            else if (isEmpty(this.editConnections)) {
                errors.push('Не заданы перетоки');
            }

            return errors;
        },
        isConnectionsValid() {
            return isEmpty(this.connectionsErrors);
        },
        connectionsAutoload: {
            get() {
                return this.$store.state.grid.connectionsAutoload;
            },
            set(value) {
                this.$store.commit('grid/setConnectionsAutoload', value);
            }
        },
        wellsErrors() {
            let errors = [];
            if (!(Boolean(this.wells) && this.wells.length > 0)) {
                errors.push('Не заданы скважины');
            }

            return errors;
        },
        isWellsValid() {
            return isEmpty(this.wellsErrors);
        },
        isSwSoValid() {
            return this.isConstantSetAndNotZero('sw') && this.isConstantSetAndNotZero('so');
        },
        isOfpValid() {
            return this.ofpConstants?.a && this.ofpConstants?.no && this.ofpConstants?.nw;
        },
        isPValid() {
            return this.isConstantSetAndNotZero('p');
        },
        fluidErrors() {
            let errors = [];
            if (!this.isConstantSetAndNotZero('mu_w')) {
                errors.push('Не задана вязкость воды mu_w');
            }

            if (!this.isConstantSetAndNotZero('mu_o')) {
                errors.push('Не задана вязкость нефти mu_o');
            }

            if (!this.isConstantSetAndNotZero('cw')) {
                errors.push('Не задана сжимаемость воды cw');
            }

            if (!this.isConstantSetAndNotZero('co')) {
                errors.push('Не задана сжимаемость нефти co');
            }

            if (this.needDensity) {
                if (!this.isConstantSetAndNotZero('rho_w')) {
                    errors.push('Не задана плотность воды rho_w');
                }

                if (!this.isConstantSetAndNotZero('rho_w_sc')) {
                    errors.push('Не задана плотность воды (ст) rho_w_sc');
                }

                if (!this.isConstantSetAndNotZero('rho_o')) {
                    errors.push('Не задана плотность нефти rho_o');
                }

                if (!this.isConstantSetAndNotZero('rho_o_sc')) {
                    errors.push('Не задана плотность нефти (ст) rho_o_sc');
                }
            }

            return errors;
        },

        isFluidValid() {
            return isEmpty(this.fluidErrors);
        },
        rockErrors() {
            let errors = [];

            let isPermeabilityValid = this.permeabilityDefinedInCells || this.isConstantSet('k');
            let isPorosityValid = this.porosityDefinedInCells || this.isConstantSet('phi');
            let isCrValid = this.isConstantSet('cr') && parseFloat(this.inputConstants['cr']) > 0;

            if (!isPermeabilityValid) {
                errors.push('Не задана проницаемость k');
            }

            if (!isPorosityValid) {
                errors.push('Не задана пористость phi');
            }

            if (!isCrValid) {
                errors.push('Не задана сжимаемость Cr');
            }

            return errors;
        },
        isRockValid() {
            return isEmpty(this.rockErrors)
        },
        injectionDataErrors() {
            let errors = [];
            if (!this.injectionData) {
                errors.push('Не задана история разработки');
                return errors;
            }

            let header = Object.keys(this.injectionData);
            let wellsOk = Boolean(header) && header.length >= this.realWells.length + 1;
            if (!wellsOk) {
                errors.push('Недостаточно скважин в истории разработки');
            }

            return errors;
        },
        isInjectionDataValid() {
            return isEmpty(this.injectionDataErrors);
        },
        bhpDataErrors() {
            let errors = [];
            if (!this.bhpData) {
                errors.push('Не задана история давлений');
                return errors;
            }

            let header = Object.keys(this.bhpData);
            let wellsOk = Boolean(header) && header.length >= this.realWells.length + 1;
            if (!wellsOk) {
                errors.push('Недостаточно скважин в истории давлений');
            }

            return errors;
        },
        isBhpDataValid() {
            return isEmpty(this.bhpDataErrors);
        },
        historyDataErrors() {
            let errors = [];
            if (!this.historyData) {
                errors.push('Не задана история добычи');
                return errors;
            }

            let header = Object.keys(this.historyData);
            let producersCount = 0;
            let wellsOk = Boolean(header) && header.length >= producersCount;

            if (!wellsOk) {
                errors.push('Недостаточно скважин в истории добычи');
            }

            return errors;
        },
        isHistoryDataValid() {
            return isEmpty(this.historyDataErrors);
        },
        isValid() {
            return this.isGridValid && this.isShapeValid && this.isWellsValid &&
                this.isConnectionsValid && this.isSwSoValid && this.isPValid &&
                this.isFluidValid && this.isRockValid && this.isInjectionDataValid &&
                this.isHistoryDataValid;
        },
        isUploading() {
            return this.$store.state.grid.loading;
        },
        gridVolume() {
            let parsedVolume = this.gridProperties?.volume;
            if (parsedVolume > 0) {
                return parsedVolume;
            }

            if (this.gridCellSize && this.gridCellSize.length > 0 && this.gridSize && this.gridSize.length > 0) {
                let cellVolume = this.gridCellSize[0] * this.gridCellSize[1] * this.gridCellSize[2];
                let totalCells = this.gridSize[0] * this.gridSize[1] * this.gridSize[2];
                return cellVolume * totalCells;
            }

            return null;
        },
        needDensity() {
            return true;
        },
        needRelativePermCoeffs() {
            return true;
        },
        unsavedProblem() {
            return this.makeProblemFromLoadedData();
        },
        state() {
            if (!this.$data) {
                return null;
            }

            let state = {'id': this?.item?.id};
            for (let key in this.$data) {
                if (typeof(this.$data[key]) !== 'function') {
                    if (!this.excludeFromState.includes(key)) {
                        if (key === 'wells') {
                            state['wells'] = this.jsonWells;
                        }
                        else if (key === 'editNodes') {
                            state['editNodes'] = this.editNodes.map(node => node.json());
                        }
                        else {
                            state[key] = this.$data[key];
                        }
                    }
                }
            }

            if (this.permeabilityDefinedInCells) {
                state['inputConstants']['k'] = this.meanCellPermeability;
            }

            if (this.porosityDefinedInCells) {
                state['inputConstants']['phi'] = this.meanCellPorosity;
            }

            state['gridProperties'] = this.gridProperties;
            if (state['gridProperties']) {
                if (typeof state['gridProperties']['ijk_index'] !== "undefined") {
                    delete state['gridProperties']['ijk_index'];
                }

                if (typeof state['gridProperties']['face_to_cell_index'] !== "undefined") {
                    delete state['gridProperties']['face_to_cell_index'];
                }
            }

            state['allConstants'] = this.allConstants;
            state['connectionsAutoload'] = this.connectionsAutoload;
            state['fontSize'] = this.fontSize;
            state['wellMarkerSize'] = this.wellMarkerSize;

            return state;
        },
        allConstants() {
            let constants = Object.assign({}, this.inputConstants);
            constants = Object.assign(constants, this.ofpConstants);
            return constants;
        },
        inputTab() {
            return this.$route?.query?.tab || 'upload';
        },
        fontSize: {
            get() {
                return this.$store.state.grid.fontSize;
            },
            set(value) {
                this.$store.commit('grid/setFontSize', value);
            }
        },
        wellMarkerSize: {
            get() {
                return this.$store.state.grid.wellMarkerSize;
            },
            set(value) {
                this.$store.commit('grid/setWellMarkerSize', value);
            }
        },
        wellCopyItems() {
            return this.itemsLoading
                ? []
                : this.$store.state?.problem?.list || [];
        },
        allErrors() {
            let errors = [];
            if (this.gridErrors) {
                for (let error of this.gridErrors) {
                    errors.push({'module': 'grid', 'error': error});
                }
            }

            if (this.shapeErrors) {
                for (let error of this.shapeErrors) {
                    errors.push({'module': 'shape', 'error': error});
                }
            }

            if (this.connectionsErrors) {
                for (let error of this.connectionsErrors) {
                    errors.push({'module': 'shape', 'error': error});
                }
            }

            if (this.wellsErrors) {
                for (let error of this.wellsErrors) {
                    errors.push({'module': 'wells', 'error': error});
                }
            }

            if (!this.isConstantSetAndNotZero('sw')) {
                errors.push({'module': 'swso', 'error': 'Не задана неуменьшаемая водонасыщенность Sw'});
            }

            if (!this.isConstantSetAndNotZero('so')) {
                errors.push({'module': 'swso', 'error': 'Не задана остаточная водонасыщенность So'});
            }

            if (!this.ofpConstants?.a) {
                errors.push({'module': 'ofp', 'error': 'Не задана a'});
            }

            if (!this.ofpConstants?.no) {
                errors.push({'module': 'ofp', 'error': 'Не задана no'});
            }

            if (!this.ofpConstants?.nw) {
                errors.push({'module': 'ofp', 'error': 'Не задана nw'});
            }

            if (!this.isConstantSetAndNotZero('p')) {
                errors.push({'module': 'p', 'error': 'Не задано давление пласта Pпл'});
            }

            if (this.fluidErrors) {
                for (let error of this.fluidErrors) {
                    errors.push({'module': 'fluid', 'error': error});
                }
            }

            if (this.injectionDataErrors) {
                for (let error of this.injectionDataErrors) {
                    errors.push({'module': 'injectionData', 'error': error});
                }
            }

            if (this.historyDataErrors) {
                for (let error of this.historyDataErrors) {
                    errors.push({'module': 'historyData', 'error': error});
                }
            }

            if (this.bhpDataErrors) {
                for (let error of this.bhpDataErrors) {
                    errors.push({'module': 'bhpData', 'error': error});
                }
            }

            return errors;
        },
        externalValidateGroups() {
            if (!this.externalValidateResult) {
                return {'error': [], 'warning': []};
            }

            let groupsHash = {};

            for (let line of this.externalValidateResult.split('\n')) {
                let parseLine = line.includes('ERROR') || line.includes('WARNING') || line.includes('AssertionError');
                if (!parseLine) {
                    continue;
                }

                let lineHash = line.replace(/[\d.]+/g, '*');
                if (!groupsHash[lineHash]) {
                    groupsHash[lineHash] = [];
                }

                groupsHash[lineHash].push(line);
            }

            let groups = {'error': [], 'warning': []};
            for (let hash in groupsHash) {
                let type = hash.includes('ERROR') || hash.includes('AssertionError')
                    ? 'error'
                    : 'warning';

                groups[type].push({
                    title: hash,
                    items: groupsHash[hash] || []
                });
            }

            return groups;
        },
        joinedInjectionHistoryData() {
            if (!this.injectionData || !this.historyData) {
                return null;
            }

            if (!this.wells) {
                return null;
            }


            let wellIds = this.wells
                .filter(well => well.type !== TYPE_IMAGINARY)
                .map(well => well.id);
            let timesteps = this.injectionData['t'] || this.historyData['t'] || null;
            if (!timesteps) {
                return null;
            }

            let joinedData = {};
            for (let wellId of wellIds) {
                joinedData[wellId] = [];

                for (let index in timesteps) {
                    index = parseInt(index);
                    let injection = this.injectionData[wellId]
                        ? this.injectionData[wellId][index] || 0
                        : null;

                    let history = this.historyData[wellId]
                        ? this.historyData[wellId][index] || 0
                        : null;

                    if (!injection && history > 0) {
                        injection = -history;
                    }

                    joinedData[wellId].push(injection);
                }
            }

            return joinedData;
        }
    }
}
</script>

<style lang="scss">
.steps-form {
    height: 100%;

    .v-tabs-items .v-window__container {
        height: 100%;
        .v-window-item, .v-window-item .v-card, .v-window-item .v-card .v-card__text {
            height: 100%;
        }
    }

    .v-tabs-bar {
        width: 250px;
        border-right: 1px solid rgba(0, 0, 0, 0.12);

        .v-slide-group__content {
            align-items: flex-start;
        }
    }



    .v-input__prepend-outer, .v-input__append-outer {
        white-space: nowrap;
    }

    .v-footer {
        width: 100%;
    }

    .v-tabs {
        height: 100%;
        border-bottom: 0;
    }

    .theme--light.v-footer {
        background-color: #ffffff;
    }
}

.text--small {
    font-size: 80%;
}

.grid-wrapper {
    position: relative;
    flex: 1 0 0;
    overflow: hidden;
}

</style>
